import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { fetchClients } from "../../../../services/clientApi";
import { fetchProjects } from "../../../../services/projectApi";
import { getUsers, fetchUserRoleCounts, fetchDashboardUsers } from "../../../../services/userMgmtApi";
import { fetchResourceAllocations } from "../../../../services/resourceAllocationApi";
import { fetchAssignments } from "../../../../services/assignmentApi";

/* ── helpers ─────────────────────────────────────────────────────── */
function getMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt   = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  return `${fmt(start)} - ${fmt(end)}`;
}

function padTwo(n) {
  return String(n).padStart(2, "0");
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/* ── current user helpers ────────────────────────────────────────── */
function getCurrentUserRole() {
  try {
    const raw = localStorage.getItem("userDetails");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return (parsed?.roleName || parsed?.role || "").toLowerCase();
  } catch {
    return "";
  }
}

function isAMorHoD(roleName) {
  return roleName.includes("account") || roleName.includes("hod") || roleName.includes("head");
}

/* ── stat card config ────────────────────────────────────────────── */
const CARD_DEFS = [
  {
    key: "clients",
    label: "Clients",
    icon: "👥",
    gradient: "linear-gradient(135deg, #d4f5e9 0%, #a8e6cf 100%)",
    iconBg: "#1db954",
    border: "#1db954",
  },
  {
    key: "projects",
    label: "Projects",
    icon: "📁",
    gradient: "linear-gradient(135deg, #e0d7f8 0%, #c3b1e1 100%)",
    iconBg: "#7c4dff",
    border: "#7c4dff",
  },
  {
    key: "accountManager",
    label: "Account Manager",
    icon: "👤",
    gradient: "linear-gradient(135deg, #fff3cd 0%, #ffe08a 100%)",
    iconBg: "#f59e0b",
    border: "#f59e0b",
  },
  
  {
    key: "resource",
    label: "Resource",
    icon: "📅",
    gradient: "linear-gradient(135deg, #dce8ff 0%, #a8c4f5 100%)",
    iconBg: "#1565c0",
    border: "#1565c0",
  },
];

/* ── main component ──────────────────────────────────────────────── */
const ResourceDashboard = () => {
  const navigate = useNavigate();

  const [clients,          setClients]          = useState([]);
  const [projects,         setProjects]         = useState([]);
  const [users,            setUsers]            = useState([]);
  const [roleCounts,       setRoleCounts]       = useState({ accountManager: 0, resource: 0, hod: 0 });
  const [roleCountsLoaded, setRoleCountsLoaded] = useState(false);
  const [loading,          setLoading]          = useState(true);

  const [activeCard,   setActiveCard]   = useState("clients");
  const [searchText,   setSearchText]   = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page,         setPage]         = useState(1);

  const [selectedProject,    setSelectedProject]    = useState(null);
  const [projectAllocations, setProjectAllocations] = useState([]);
  const [allocLoading,       setAllocLoading]       = useState(false);
  const [expandedProjectId,  setExpandedProjectId]  = useState(null);
  const [projectAssignments, setProjectAssignments] = useState([]);

  const [selectedResource,     setSelectedResource]     = useState(null);
  const [resourceProjects,     setResourceProjects]     = useState([]);
  const [resourceModalLoading, setResourceModalLoading] = useState(false);

  const [listingAssignments,        setListingAssignments]        = useState([]);
  const [listingAssignmentsLoading, setListingAssignmentsLoading] = useState(false);

  const PAGE_SIZE = 10;

  // Detect if the logged-in user is AM or HoD
  const currentUserRole = useMemo(() => getCurrentUserRole(), []);
  const canSeeMyClients = useMemo(() => isAMorHoD(currentUserRole), [currentUserRole]);
  const isSuperAdmin    = useMemo(() => currentUserRole.includes("super"), [currentUserRole]);

  /* ── fetch all data once ─────────────────────────────────────────── */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, u, rc] = await Promise.all([
        fetchClients().catch(() => []),
        fetchProjects().catch(() => []),
        getUsers().catch(() => []),
        fetchUserRoleCounts().catch(() => null),
      ]);
      setClients(c);
      setProjects(p);
      // If getUsers() returned empty (no user:view permission), fall back to
      // the dashboard-listing endpoint which is accessible to all roles.
      if (u.length > 0) {
        setUsers(u);
      } else {
        const dl = await fetchDashboardUsers().catch(() => []);
        setUsers(dl);
      }
      if (rc) { setRoleCounts(rc); setRoleCountsLoaded(true); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* fetch all assignments once when AM/HoD opens the resource card */
  useEffect(() => {
    if (activeCard !== "resource" || !canSeeMyClients) return;
    let cancelled = false;
    setListingAssignmentsLoading(true);
    fetchAssignments()
      .then((list) => { if (!cancelled) setListingAssignments(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setListingAssignments([]); })
      .finally(() => { if (!cancelled) setListingAssignmentsLoading(false); });
    return () => { cancelled = true; };
  }, [activeCard, canSeeMyClients]);

  /* employeeId → deduplicated array of project names */
  const resourceProjectMap = useMemo(() => {
    const map = {};
    for (const asgn of listingAssignments) {
      const pid = (asgn.projectId?._id || asgn.projectId || "").toString();
      const projectName =
        asgn.projectId?.projectName ||
        projects.find((p) => (p._id || "").toString() === pid)?.projectName ||
        null;
      if (!projectName) continue;
      for (const res of (asgn.resources || [])) {
        const empId = (res.employeeId?._id || res.employeeId || "").toString();
        if (!empId) continue;
        if (!map[empId]) map[empId] = new Set();
        map[empId].add(projectName);
      }
    }
    return map;
  }, [listingAssignments, projects]);

  /* ── active project count per client (computed from loaded data) ─── */
  const clientProjectCounts = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      if (!p.isCompleted && p.isActive !== false) {
        const cid = (p.clientId?._id || p.clientId || "").toString();
        if (cid) map[cid] = (map[cid] || 0) + 1;
      }
    });
    return map;
  }, [projects]);

  /* ── counts ──────────────────────────────────────────────────────── */
  const counts = useMemo(() => {
    const roleName = (u) => (u.roleId?.roleName || u.roleName || u.role || "").toLowerCase();
    // Always prefer the dedicated stats API (uses explicit DB populate, works for any role).
    // Fall back to counting from the users array only when the stats API didn't load.
    const activeProjectCount = projects.filter((p) => !p.isCompleted && p.isActive !== false).length;
    if (roleCountsLoaded) {
      return {
        clients:        clients.length,
        projects:       activeProjectCount,
        accountManager: roleCounts.accountManager,
        hod:            roleCounts.hod,
        resource:       roleCounts.resource,
      };
    }
    // Fallback: count from the users list (only works when user has user:view permission)
    return {
      clients:        clients.length,
      projects:       activeProjectCount,
      accountManager: users.filter((u) => roleName(u).includes("account")).length,
      hod:            users.filter((u) => roleName(u).includes("hod") || roleName(u).includes("head")).length,
      resource:       users.filter((u) => roleName(u).includes("resource") || roleName(u).includes("employee")).length,
    };
  }, [clients, projects, users, roleCounts, roleCountsLoaded]);

  /* ── table rows for active card ──────────────────────────────────── */
  const tableRows = useMemo(() => {
    const roleName = (u) => (u.roleId?.roleName || u.roleName || u.role || "").toLowerCase();
    switch (activeCard) {
      case "clients":        return clients;
      case "projects":       return projects.filter((p) => !p.isCompleted && p.isActive !== false);
      case "accountManager": return users.filter((u) => roleName(u).includes("account"));
      case "hod":            return users.filter((u) => roleName(u).includes("hod") || roleName(u).includes("head"));
      case "resource":       return users.filter((u) => u.isActive !== false && (roleName(u).includes("resource") || roleName(u).includes("employee")));
      default:               return [];
    }
  }, [activeCard, clients, projects, users]);

  const filteredRows = useMemo(() => {
    const q = searchText.toLowerCase();
    return tableRows.filter((row) => {
      const text = Object.values(row).join(" ").toLowerCase();
      if (q && !text.includes(q)) return false;
      if (statusFilter === "active"   && row.isActive === false) return false;
      if (statusFilter === "inactive" && row.isActive !== false) return false;
      return true;
    });
  }, [tableRows, searchText, statusFilter]);

  const totalPages  = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* reset page + expanded row on filter/card change */
  useEffect(() => { setPage(1); setExpandedProjectId(null); }, [activeCard, searchText, statusFilter]);

  const openProjectDetail = useCallback(async (project) => {
    setSelectedProject(project);
    setProjectAllocations([]);
    setAllocLoading(true);
    try {
      const result = await fetchResourceAllocations({ projectId: project._id });
      const list = Array.isArray(result) ? result : (result.data ?? []);
      setProjectAllocations(list);
    } catch {
      setProjectAllocations([]);
    } finally {
      setAllocLoading(false);
    }
  }, []);

  const handleProjectRowClick = useCallback(async (project) => {
    if (canSeeMyClients) {
      const newId = expandedProjectId === project._id ? null : project._id;
      setExpandedProjectId(newId);
      if (!newId) return;
      setProjectAssignments([]);
      setAllocLoading(true);
      try {
        const list = await fetchAssignments({ projectId: project._id });
        setProjectAssignments(Array.isArray(list) ? list : []);
      } catch {
        setProjectAssignments([]);
      } finally {
        setAllocLoading(false);
      }
    } else {
      openProjectDetail(project);
    }
  }, [canSeeMyClients, expandedProjectId, openProjectDetail]);

  const handleResourceRowClick = useCallback(async (user) => {
    setSelectedResource(user);
    setResourceProjects([]);
    setResourceModalLoading(true);
    try {
      const list = await fetchAssignments({ employeeId: user._id });
      const assignments = Array.isArray(list) ? list : [];
      const seen = new Set();
      const projectNames = [];
      for (const asgn of assignments) {
        const name =
          asgn.projectId?.projectName ||
          projects.find((p) => (p._id || "").toString() === (asgn.projectId || "").toString())?.projectName ||
          null;
        if (name && !seen.has(name)) { seen.add(name); projectNames.push(name); }
      }
      setResourceProjects(projectNames);
    } catch {
      setResourceProjects([]);
    } finally {
      setResourceModalLoading(false);
    }
  }, [projects]);

  /* ── table renderer ──────────────────────────────────────────────── */
  const StatusBadge = ({ active }) =>
    active === false ? (
      <span style={{
        border: "1.5px solid #e91e63", color: "#e91e63",
        borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 700,
      }}>INACTIVE</span>
    ) : (
      <span style={{
        border: "1.5px solid #1db954", color: "#1db954",
        borderRadius: 20, padding: "2px 14px", fontSize: 11, fontWeight: 700,
      }}>ACTIVE</span>
    );

  const renderClientsTable = () => {
    // AM / HoD see a focused 2-column view: Client Name + Number of Projects
    if (canSeeMyClients) {
      return (
        <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
          <thead>
            <tr style={{ color: "#6c757d", fontWeight: 700, borderBottom: "2px solid #f0f0f0" }}>
              <th className="border-0">#</th>
              <th className="border-0">Client Name</th>
              <th className="border-0" style={{ textAlign: "center" }}>Number of Projects</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((c, i) => {
              const count = clientProjectCounts[(c._id || "").toString()] || 0;
              return (
                <tr key={c._id} style={{ verticalAlign: "middle" }}>
                  <td className="border-0" style={{ color: "#94a3b8", width: 40 }}>
                    {(page - 1) * PAGE_SIZE + i + 1}
                  </td>
                  <td className="border-0 fw-semibold">{c.clientName || "—"}</td>
                  <td className="border-0" style={{ textAlign: "center" }}>
                    <span style={{
                      display: "inline-block",
                      minWidth: 32,
                      padding: "2px 12px",
                      borderRadius: 20,
                      background: count > 0 ? "#d4f5e9" : "#f1f5f9",
                      color: count > 0 ? "#1db954" : "#94a3b8",
                      fontWeight: 700,
                      fontSize: 12,
                      border: `1.5px solid ${count > 0 ? "#1db954" : "#e2e8f0"}`,
                    }}>
                      {count}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    // All other roles see the full clients table
    return (
      <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
        <thead>
          <tr style={{ color: "#6c757d", fontWeight: 700, borderBottom: "2px solid #f0f0f0" }}>
            <th className="border-0">Client Name</th>
            <th className="border-0">Contact Person</th>
            <th className="border-0">Email</th>
            <th className="border-0">Mobile</th>
            <th className="border-0">Status</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.map((c) => (
            <tr key={c._id} style={{ verticalAlign: "middle" }}>
              <td className="border-0 fw-semibold">{c.clientName || "—"}</td>
              <td className="border-0">{c.contactPerson || "—"}</td>
              <td className="border-0">{c.clientEmail || "—"}</td>
              <td className="border-0">{c.clientMobile || "—"}</td>
              <td className="border-0"><StatusBadge active={c.isActive} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderProjectsTable = () => {
    const getClientName = (project) => {
      const cid = project.clientId?._id || project.clientId || "";
      const found = clients.find((c) => (c._id || "").toString() === cid.toString());
      return found?.clientName || "—";
    };

    return (
      <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
        <thead>
          <tr style={{ color: "#6c757d", fontWeight: 700, borderBottom: "2px solid #f0f0f0" }}>
            <th className="border-0">Project Name</th>
            <th className="border-0">Client Name</th>
            <th className="border-0">Description</th>
            <th className="border-0">Completed</th>
            <th className="border-0">Progress</th>
            <th className="border-0">Status</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.map((p) => {
            const isExpanded = canSeeMyClients && expandedProjectId === p._id;
            return (
              <Fragment key={p._id}>
                <tr
                  style={{
                    verticalAlign: "middle",
                    cursor: isSuperAdmin ? undefined : "pointer",
                    background: isExpanded ? "#f5f0ff" : undefined,
                  }}
                  onClick={isSuperAdmin ? undefined : () => handleProjectRowClick(p)}
                >
                  <td className="border-0 fw-semibold">
                    {canSeeMyClients && (
                      <i
                        className={`fa fa-chevron-${isExpanded ? "down" : "right"} me-2`}
                        style={{ fontSize: 10, color: "#7c4dff" }}
                      />
                    )}
                    {p.projectName || "—"}
                  </td>
                  <td className="border-0">{getClientName(p)}</td>
                  <td className="border-0" style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.projectDescription || "—"}
                  </td>
                  <td className="border-0">{p.isCompleted ? "Yes" : "No"}</td>
                  <td className="border-0">
                    <div className="d-flex align-items-center gap-2">
                      <div className="progress flex-grow-1" style={{ height: 6 }}>
                        <div
                          className="progress-bar"
                          style={{ width: `${p.projectPercentageCompleted || 0}%`, background: "#7c4dff" }}
                        />
                      </div>
                      <span style={{ fontSize: 11, minWidth: 28 }}>{p.projectPercentageCompleted || 0}%</span>
                    </div>
                  </td>
                  <td className="border-0">
                    <StatusBadge active={!p.isCompleted} />
                  </td>
                </tr>

                {isExpanded && (
                  <tr>
                    <td colSpan={6} style={{ padding: "0 0 14px 0", border: 0, background: "#faf8ff" }}>
                      <div style={{
                        margin:       "0 16px 0 36px",
                        borderLeft:   "4px solid #7c4dff",
                        background:   "#f5f0ff",
                        borderRadius: "0 10px 10px 0",
                        padding:      16,
                      }}>
                        {allocLoading ? (
                          <div className="d-flex align-items-center gap-2" style={{ fontSize: 13, color: "#7c4dff" }}>
                            <div className="spinner-border spinner-border-sm" role="status" />
                            <span>Loading resources…</span>
                          </div>
                        ) : (() => {
                          const clientName = getClientName(p);

                          // Collect unique employees across all assignments for this project
                          const seenIds = new Set();
                          const uniqueEmployees = [];
                          for (const asgn of projectAssignments) {
                            for (const res of asgn.resources || []) {
                              const empId = (res.employeeId?._id || res.employeeId || "").toString();
                              if (!empId || seenIds.has(empId)) continue;
                              seenIds.add(empId);
                              // Use populated userName, fall back to users state
                              const name =
                                res.employeeId?.userName ||
                                users.find((u) => (u._id || "").toString() === empId)?.userName ||
                                "";
                              uniqueEmployees.push(name);
                            }
                          }

                          return (
                            <div style={{
                              display:             "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                              gap:                 "12px 32px",
                            }}>
                              {[
                                { label: "Client Name",         value: clientName },
                                { label: "Number of Resources", value: String(uniqueEmployees.length) },
                                { label: "Name of Resources",   value: uniqueEmployees.filter(Boolean).length ? uniqueEmployees.filter(Boolean).join(", ") : "—" },
                                { label: "Start Date",          value: fmtDate(p.startDate) },
                                { label: "End Date",            value: fmtDate(p.endDate) },
                              ].map(({ label, value }) => (
                                <div key={label}>
                                  <div style={{
                                    fontSize:      11,
                                    fontWeight:    700,
                                    color:         "#7c4dff",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    marginBottom:  3,
                                  }}>
                                    {label}
                                  </div>
                                  <div style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{value}</div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderResourceListingTable = () => (
    <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
      <thead>
        <tr style={{ color: "#6c757d", fontWeight: 700, borderBottom: "2px solid #f0f0f0" }}>
          <th className="border-0">#</th>
          <th className="border-0">Name of Resource</th>
          <th className="border-0">Department</th>
        </tr>
      </thead>
      <tbody>
        {currentRows.map((u, i) => {
          const deptName = u.departmentChildID?.departmentName || "—";
          return (
            <tr key={u._id} style={{ verticalAlign: "middle" }}>
              <td className="border-0" style={{ color: "#94a3b8", width: 40 }}>
                {(page - 1) * PAGE_SIZE + i + 1}
              </td>
              <td className="border-0 fw-semibold">{u.userName || "—"}</td>
              <td className="border-0">{deptName}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderUsersTable = () => {
    const clickable = canSeeMyClients && activeCard === "resource";
    return (
      <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
        <thead>
          <tr style={{ color: "#6c757d", fontWeight: 700, borderBottom: "2px solid #f0f0f0" }}>
            <th className="border-0">Name</th>
            <th className="border-0">Email</th>
            <th className="border-0">Mobile</th>
            <th className="border-0">Role</th>
            <th className="border-0">Status</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.map((u) => (
            <tr
              key={u._id}
              style={{ verticalAlign: "middle", cursor: clickable ? "pointer" : undefined }}
              onClick={clickable ? () => handleResourceRowClick(u) : undefined}
            >
              <td className="border-0 fw-semibold">{u.userName || "—"}</td>
              <td className="border-0">{u.email || "—"}</td>
              <td className="border-0">{u.mobileNumber || "—"}</td>
              <td className="border-0">{u.roleId?.roleName || u.roleName || "—"}</td>
              <td className="border-0"><StatusBadge active={u.isActive} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading…</p>
        </div>
      );
    }

    if (currentRows.length === 0) {
      return (
        <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
          No records found.
        </div>
      );
    }

    if (activeCard === "clients")  return renderClientsTable();
    if (activeCard === "projects") return renderProjectsTable();
    if (activeCard === "resource" && !isSuperAdmin) return renderResourceListingTable();
    return renderUsersTable();
  };

  const activeLabel = CARD_DEFS.find((c) => c.key === activeCard)?.label || "";
  const activeCount = filteredRows.filter((r) => r.isActive !== false).length;

  /* ── render ──────────────────────────────────────────────────────── */
  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ── Timeframe ──────────────────────────────────────────────── */}
      <p className="mb-3" style={{ fontSize: 13, color: "#555" }}>
        <span className="text-muted fw-semibold">Timeframe </span>
        <span style={{ color: "#7c4dff", fontWeight: 600 }}>{getMonthRange()}</span>
      </p>

      {/* ── Stat cards ─────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        {CARD_DEFS.map((card) => {
          const isActive = activeCard === card.key;
          const count    = counts[card.key] || 0;
          return (
            <div key={card.key} className="col-xl col-lg-4 col-sm-6">
              <div
                onClick={() => setActiveCard(card.key)}
                style={{
                  background:   card.gradient,
                  borderRadius: 16,
                  padding:      "24px 20px",
                  cursor:       "pointer",
                  border:       isActive ? `2.5px solid ${card.border}` : "2.5px solid transparent",
                  boxShadow:    isActive ? `0 4px 20px ${card.border}33` : "0 2px 8px rgba(0,0,0,0.06)",
                  transition:   "all 0.2s",
                  textAlign:    "center",
                  position:     "relative",
                  overflow:     "hidden",
                }}
              >
                {/* decorative circle */}
                <div style={{
                  position: "absolute", top: -18, right: -18,
                  width: 80, height: 80, borderRadius: "50%",
                  background: `${card.iconBg}22`,
                }} />
                <div style={{
                  width: 52, height: 52, borderRadius: "50%",
                  background: card.iconBg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 12px",
                  fontSize: 22,
                  boxShadow: `0 4px 12px ${card.iconBg}55`,
                }}>
                  {card.icon}
                </div>
                <div style={{ fontSize: 36, fontWeight: 800, color: "#1a1a2e", lineHeight: 1 }}>
                  {loading ? "—" : padTwo(count)}
                </div>
                <div style={{ fontSize: 13, color: "#555", marginTop: 6, fontWeight: 500 }}>
                  {card.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Table section ──────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-4">

          {/* Table header row */}
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-0 fw-bold" style={{ color: "#1a1a2e" }}>{activeLabel}</h5>
              <p className="mb-0" style={{ fontSize: 12, color: "#e91e63", fontWeight: 600 }}>
                {padTwo(activeCount)} {activeLabel} Active
              </p>
            </div>
          </div>

          {/* Search + filter bar */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div className="input-group" style={{ maxWidth: 260 }}>
              <span className="input-group-text bg-white border-end-0" style={{ borderRadius: "8px 0 0 8px" }}>
                <i className="fa fa-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder={`Search ${activeLabel}…`}
                style={{ borderRadius: "0 8px 8px 0", fontSize: 13 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div className="dropdown">
              <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0" style={{ borderRadius: 10 }}>
                {[
                  { value: "all",      label: "All" },
                  { value: "active",   label: "Active only" },
                  { value: "inactive", label: "Inactive only" },
                ].map((opt) => (
                  <li key={opt.value}>
                    <button
                      className={`dropdown-item ${statusFilter === opt.value ? "active" : ""}`}
                      style={{ fontSize: 13 }}
                      onClick={() => setStatusFilter(opt.value)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">{renderTable()}</div>

          {/* Pagination */}
          {!loading && filteredRows.length > 0 && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-2"
              style={{ borderTop: "1px solid #f0f0f0", fontSize: 13, color: "#6c757d" }}>
              <span>
                Rows per page : {PAGE_SIZE} &nbsp;▼&nbsp;&nbsp;
                {(page - 1) * PAGE_SIZE + 1} – {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
              </span>
              <div className="d-flex gap-1">
                <button
                  className="btn btn-sm btn-light border"
                  style={{ borderRadius: 6 }}
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <i className="fa fa-chevron-left" />
                </button>
                <button
                  className="btn btn-sm btn-light border"
                  style={{ borderRadius: 6 }}
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <i className="fa fa-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Resource Detail Modal */}
      <Modal show={!!selectedResource} onHide={() => setSelectedResource(null)} centered>
        <Modal.Header closeButton className="border-bottom pb-3">
          <Modal.Title style={{ fontSize: 16 }}>
            <i className="fa fa-user me-2 text-primary" />
            Resource Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedResource && (
            <div style={{
              borderLeft:   "4px solid #1565c0",
              background:   "#eef4ff",
              borderRadius: "0 10px 10px 0",
              padding:      18,
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1565c0", marginBottom: 14 }}>
                <i className="fa fa-user me-2" />
                {selectedResource.userName || "—"}
              </div>
              {[
                { label: "Name of Resource", value: selectedResource.userName || "—" },
                {
                  label: "Project Name",
                  value: resourceModalLoading
                    ? "Loading…"
                    : resourceProjects.length ? resourceProjects.join(", ") : "No projects assigned",
                },
              ].map(({ label, value }) => (
                <div key={label} className="mb-2" style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <strong style={{ minWidth: 140, flexShrink: 0, color: "#334155" }}>{label}:</strong>
                  <span style={{ color: "#495057" }}>{value}</span>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedResource(null)}>Close</button>
        </Modal.Footer>
      </Modal>

      {/* Project Detail Modal */}
      <Modal show={!!selectedProject} onHide={() => setSelectedProject(null)} centered>
        <Modal.Header closeButton className="border-bottom pb-3">
          <Modal.Title style={{ fontSize: 16 }}>
            <i className="fa fa-folder-open me-2 text-primary" />
            Project Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedProject && (() => {
            const clientName = (() => {
              const cid = selectedProject.clientId?._id || selectedProject.clientId || "";
              const found = clients.find((c) => (c._id || "").toString() === cid.toString());
              return found?.clientName || "—";
            })();

            const resourceNames = allocLoading
              ? null
              : projectAllocations.map((a) => a.resourceId?.firstName || a.resourceId?.userName || "").filter(Boolean);

            return (
              <div style={{
                borderLeft:   "4px solid #7c4dff",
                background:   "#f5f0ff",
                borderRadius: "0 10px 10px 0",
                padding:      18,
              }}>
                {/* Project Name */}
                <div style={{ fontSize: 15, fontWeight: 700, color: "#7c4dff", marginBottom: 14 }}>
                  <i className="fa fa-folder me-2" />
                  {selectedProject.projectName || "—"}
                </div>

                {[
                  { label: "Client Name",         value: clientName },
                  { label: "Number of Resources", value: allocLoading ? "Loading…" : String(projectAllocations.length) },
                  { label: "Name of Resources",   value: allocLoading ? "Loading…" : (resourceNames?.length ? resourceNames.join(", ") : "—") },
                  { label: "Start Date",          value: fmtDate(selectedProject.startDate) },
                  { label: "End Date",            value: fmtDate(selectedProject.endDate) },
                ].map(({ label, value }) => (
                  <div key={label} className="mb-2" style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <strong style={{ minWidth: 150, flexShrink: 0, color: "#334155" }}>{label}:</strong>
                    <span style={{ color: "#495057" }}>{value}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </Modal.Body>

        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedProject(null)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResourceDashboard;
